import React from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { runtimeProviderConfigService, runtimeToolsService } from '../../services/runtimeOperationalService';
import { runtimeQueryKeys } from '../../services/runtimeQueryKeys';
import useRuntimeQuery from '../../hooks/useRuntimeQuery';
import useRuntimeMutation from '../../hooks/useRuntimeMutation';
import useRuntimeActionLock from '../../hooks/useRuntimeActionLock';
import { RuntimeErrorState } from '../common/runtime';
import { buildApiErrorMessage } from '../../utils/apiError';

const { Text } = Typography;

const BILLING_TYPE_OPTIONS = [
  { label: 'BOLETO', value: 'BOLETO' },
  { label: 'PIX', value: 'PIX' },
  { label: 'CREDIT_CARD', value: 'CREDIT_CARD' },
];

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }
  return parsed.toLocaleString('pt-BR');
};

const PlatformRuntimeRealChargeTester = () => {
  const [form] = Form.useForm();
  const { withActionLock, isLocked } = useRuntimeActionLock();
  const [lastResult, setLastResult] = React.useState(null);
  const [lastProviderStatus, setLastProviderStatus] = React.useState(null);
  const [chargesTenantId, setChargesTenantId] = React.useState('');
  const [selectedCharge, setSelectedCharge] = React.useState(null);

  const globalProviderQuery = useRuntimeQuery({
    queryKey: runtimeQueryKeys.providerGlobalConfig(),
    queryFn: async () => {
      const response = await runtimeProviderConfigService.getGlobalConfig();
      return response.data;
    },
  });

  const createChargeMutation = useRuntimeMutation({
    mutationFn: async ({ tenantId, invoiceId, payload }) => {
      const response = await runtimeToolsService.runCharge(tenantId, invoiceId, payload);
      return response.data;
    },
  });

  const createInvoiceMutation = useRuntimeMutation({
    mutationFn: async ({ tenantId }) => {
      const response = await runtimeToolsService.runInvoiceDryRun(tenantId);
      return response.data;
    },
  });

  const getProviderStatusMutation = useRuntimeMutation({
    mutationFn: async ({ tenantId, chargeId }) => {
      const response = await runtimeToolsService.getChargeProviderStatus(tenantId, chargeId);
      return response.data;
    },
  });

  const tenantChargesQuery = useRuntimeQuery({
    queryKey: ['runtime', 'tenant-charges', String(chargesTenantId || '').trim()],
    enabled: Boolean(String(chargesTenantId || '').trim()),
    queryFn: async () => {
      const response = await runtimeToolsService.listTenantCharges(String(chargesTenantId || '').trim());
      return response.data;
    },
  });

  const lockKeyCreate = 'runtime-real-charge:create';
  const lockKeyStatus = 'runtime-real-charge:status';
  const lockKeyLoadCharges = 'runtime-real-charge:load-charges';

  const onSubmit = async () => {
    const values = await form.validateFields();

    if (!values.real_confirm) {
      message.error('Confirme explicitamente a operacao real para continuar.');
      return;
    }

    const tenantId = String(values.tenant_id || '').trim();
    let invoiceId = String(values.invoice_id || '').trim();

    if (!invoiceId) {
      if (!values.create_invoice_if_missing) {
        message.error('Informe invoice_id ou habilite a criacao automatica de invoice.');
        return;
      }
      const invoiceResult = await createInvoiceMutation.mutateAsync({ tenantId });
      invoiceId = String(invoiceResult?.id || '').trim();
      if (!invoiceId) {
        throw new Error('Nao foi possivel resolver invoice_id para criar a cobranca real.');
      }
    }

    const customer = {
      name: String(values.customer_name || '').trim(),
      cpf_cnpj: String(values.customer_cpf_cnpj || '').trim(),
      email: String(values.customer_email || '').trim() || undefined,
      phone: String(values.customer_phone || '').trim() || undefined,
      external_reference: String(values.customer_external_reference || '').trim() || undefined,
    };

    const payload = {
      provider_key: String(values.provider_key || 'asaas_billing').trim(),
      customer,
      billing_type: String(values.billing_type || 'BOLETO').trim().toUpperCase(),
      due_date: values.due_date ? dayjs(values.due_date).format('YYYY-MM-DD') : undefined,
      description: String(values.description || '').trim() || undefined,
      idempotency_key: String(values.idempotency_key || '').trim() || undefined,
    };

    const result = await createChargeMutation.mutateAsync({
      tenantId,
      invoiceId,
      payload,
    });

    setLastResult({
      ...result,
      tenant_id: tenantId,
      invoice_id: invoiceId,
      created_at_local: new Date().toISOString(),
    });
    setSelectedCharge(result);
    setLastProviderStatus(null);
    if (String(chargesTenantId || '').trim() === tenantId && tenantChargesQuery.refetch) {
      await tenantChargesQuery.refetch();
    }
    message.success('Cobranca SaaS real enviada com sucesso (ambiente sandbox/ASAAS).');
  };

  const onCreateRealCharge = async () => {
    try {
      const run = await withActionLock(lockKeyCreate, async () => {
        await onSubmit();
      });
      if (run?.skipped) {
        return;
      }
    } catch (error) {
      const apiError = buildApiErrorMessage(error, 'Falha ao criar cobranca SaaS real.');
      const normalizedMessage = String(apiError.message || '');
      const friendlyMessage = normalizedMessage.includes('Credencial ASAAS Billing não configurada')
        || normalizedMessage.includes('Credencial ASAAS SaaS Billing da plataforma não configurada')
        ? 'Credencial ASAAS SaaS Billing da plataforma não configurada.'
        : normalizedMessage;
      message.error(friendlyMessage);
    }
  };

  const onCheckProviderStatus = async () => {
    const activeCharge = selectedCharge || lastResult;
    const tenantId = String(activeCharge?.tenant_id || '').trim();
    const chargeId = String(activeCharge?.id || '').trim();
    if (!tenantId || !chargeId) {
      message.error('Nao ha cobranca para consultar status do provider.');
      return;
    }

    try {
      const run = await withActionLock(lockKeyStatus, async () => {
        const status = await getProviderStatusMutation.mutateAsync({ tenantId, chargeId });
        setLastProviderStatus(status);
      });
      if (run?.skipped) {
        return;
      }
      message.success('Status do provider consultado com sucesso.');
    } catch (error) {
      const apiError = buildApiErrorMessage(error, 'Falha ao consultar status do provider.');
      message.error(apiError.message);
    }
  };

  const onLoadTenantCharges = async () => {
    const tenantId = String(form.getFieldValue('tenant_id') || '').trim();
    if (!tenantId) {
      message.error('Informe tenant_id para carregar cobrancas recentes.');
      return;
    }

    const run = await withActionLock(lockKeyLoadCharges, async () => {
      setChargesTenantId(tenantId);
      setSelectedCharge(null);
      setLastProviderStatus(null);
    });
    if (run?.skipped) {
      return;
    }
  };

  const onSelectChargeAndCheckStatus = async (record) => {
    if (!record) {
      return;
    }
    const tenantId = String(chargesTenantId || '').trim();
    const chargeId = String(record.id || '').trim();
    if (!tenantId || !chargeId) {
      message.error('Cobranca invalida para consulta de status.');
      return;
    }

    setSelectedCharge({ ...record, tenant_id: tenantId });
    try {
      const run = await withActionLock(lockKeyStatus, async () => {
        const status = await getProviderStatusMutation.mutateAsync({ tenantId, chargeId });
        setLastProviderStatus(status);
      });
      if (run?.skipped) {
        return;
      }
      message.success('Status do provider consultado com sucesso.');
    } catch (error) {
      const apiError = buildApiErrorMessage(error, 'Falha ao consultar status do provider.');
      message.error(apiError.message);
    }
  };

  const tenantCharges = Array.isArray(tenantChargesQuery.data?.items) ? tenantChargesQuery.data.items : [];

  const chargesColumns = [
    {
      title: 'Charge ID',
      dataIndex: 'id',
      key: 'id',
      render: (value) => <Text copyable={{ text: String(value || '') }}>{value || '-'}</Text>,
    },
    {
      title: 'Invoice ID',
      dataIndex: 'invoice_id',
      key: 'invoice_id',
    },
    {
      title: 'Provider',
      dataIndex: 'provider_key',
      key: 'provider_key',
    },
    {
      title: 'External Ref ASAAS',
      dataIndex: 'external_charge_ref',
      key: 'external_charge_ref',
      render: (value) => (value ? <Text copyable={{ text: String(value) }}>{value}</Text> : '-'),
    },
    {
      title: 'Status local',
      dataIndex: 'status',
      key: 'status',
      render: (value) => String(value || '-').toUpperCase(),
    },
    {
      title: 'Status provider raw',
      dataIndex: 'provider_status_raw',
      key: 'provider_status_raw',
      render: (value) => value || '-',
    },
    {
      title: 'Valor',
      key: 'amount',
      render: (_, row) => `${Number(row.amount_cents || 0) / 100} ${row.currency_code || ''}`,
    },
    {
      title: 'Criada em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, record) => (
        <Button size="small" onClick={() => onSelectChargeAndCheckStatus(record)}>
          Consultar status provider
        </Button>
      ),
    },
  ];

  const globalProvider = globalProviderQuery.data || null;
  const globalProviderError = globalProviderQuery.isError
    ? buildApiErrorMessage(globalProviderQuery.error, 'Falha ao carregar provider efetivo global.')
    : null;

  return (
    <Card title="Criar Cobranca SaaS Real (Sandbox)">
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="error"
          showIcon
          message="Operacao real ASAAS/Sandbox"
          description="Use apenas para teste operacional controlado de Platform Admin. Esta acao tenta criar cobranca real no provider configurado."
        />

        {globalProviderError ? (
          <RuntimeErrorState
            title="Falha ao carregar provider efetivo"
            description={globalProviderError.message}
            onRetry={() => globalProviderQuery.refetch()}
          />
        ) : null}

        {globalProvider ? (
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Provider efetivo global">
              {globalProvider.provider_display_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Provider key">{globalProvider.provider_key || '-'}</Descriptions.Item>
            <Descriptions.Item label="Source">{globalProvider.source || '-'}</Descriptions.Item>
            <Descriptions.Item label="Enabled">
              {globalProvider.enabled ? <Tag color="green">SIM</Tag> : <Tag color="red">NAO</Tag>}
            </Descriptions.Item>
          </Descriptions>
        ) : null}

        <Form
          form={form}
          layout="vertical"
          initialValues={{
            provider_key: 'asaas_billing',
            billing_type: 'BOLETO',
            create_invoice_if_missing: true,
            real_confirm: false,
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item
              label="Tenant ID"
              name="tenant_id"
              rules={[{ required: true, message: 'Informe o tenant_id.' }]}
            >
              <Input placeholder="tenant_id" />
            </Form.Item>

            <Form.Item label="Invoice ID (opcional)" name="invoice_id">
              <Input placeholder="invoice_id existente; deixe vazio para criar automaticamente" />
            </Form.Item>

            <Form.Item label="Criar invoice automaticamente se nao informado" name="create_invoice_if_missing" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item
              label="Provider"
              name="provider_key"
              rules={[{ required: true, message: 'Selecione o provider.' }]}
            >
              <Select
                options={[
                  { label: 'ASAAS (SaaS Billing)', value: 'asaas_billing' },
                  { label: 'ASAAS (alias)', value: 'asaas' },
                  { label: 'Mock (dry-run)', value: 'mock' },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Nome do pagador"
              name="customer_name"
              rules={[{ required: true, message: 'Informe o nome do pagador.' }]}
            >
              <Input placeholder="Nome completo" />
            </Form.Item>

            <Form.Item
              label="CPF/CNPJ"
              name="customer_cpf_cnpj"
              rules={[{ required: true, message: 'Informe CPF/CNPJ.' }]}
            >
              <Input placeholder="Somente numeros ou formatado" />
            </Form.Item>

            <Form.Item label="E-mail (opcional)" name="customer_email">
              <Input placeholder="email@exemplo.com" />
            </Form.Item>

            <Form.Item label="Telefone (opcional)" name="customer_phone">
              <Input placeholder="(11) 98888-7777" />
            </Form.Item>

            <Form.Item label="Customer external reference (opcional)" name="customer_external_reference">
              <Input placeholder="referencia idempotente de cliente" />
            </Form.Item>

            <Form.Item label="Vencimento (opcional)" name="due_date">
              <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="Billing Type" name="billing_type">
              <Select options={BILLING_TYPE_OPTIONS} />
            </Form.Item>

            <Form.Item label="Descricao (opcional)" name="description">
              <Input placeholder="Descricao da cobranca" />
            </Form.Item>

            <Form.Item label="Idempotency Key (opcional)" name="idempotency_key">
              <Input placeholder="chave idempotente da cobranca" />
            </Form.Item>

            <Form.Item
              label="Confirmacao explicita"
              name="real_confirm"
              valuePropName="checked"
              tooltip="Necessario para evitar disparo acidental de operacao real"
            >
              <Switch checkedChildren="CONFIRMADO" unCheckedChildren="PENDENTE" />
            </Form.Item>

            <Space>
              <Button
                type="primary"
                danger
                onClick={onCreateRealCharge}
                loading={createChargeMutation.isPending || createInvoiceMutation.isPending}
                disabled={isLocked(lockKeyCreate)}
              >
                Criar cobranca SaaS real
              </Button>
              <Button
                onClick={onCheckProviderStatus}
                loading={getProviderStatusMutation.isPending}
                disabled={isLocked(lockKeyStatus) || !(selectedCharge?.id || lastResult?.id)}
              >
                Consultar status provider
              </Button>
              <Button
                onClick={onLoadTenantCharges}
                loading={tenantChargesQuery.isFetching}
                disabled={isLocked(lockKeyLoadCharges)}
              >
                Carregar cobrancas do tenant
              </Button>
            </Space>
          </Space>
        </Form>

        <Card type="inner" title="Cobrancas recentes do tenant">
          {!chargesTenantId ? (
            <Text type="secondary">Informe tenant_id e clique em Carregar cobrancas do tenant.</Text>
          ) : null}
          {chargesTenantId ? (
            <Table
              rowKey={(record) => String(record.id || '')}
              columns={chargesColumns}
              dataSource={tenantCharges}
              loading={tenantChargesQuery.isLoading || tenantChargesQuery.isFetching}
              pagination={{ pageSize: 5, hideOnSinglePage: true }}
            />
          ) : null}
        </Card>

        {lastResult ? (
          <Card type="inner" title="Ultima cobranca criada">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Tenant ID">{lastResult.tenant_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="Invoice ID">{lastResult.invoice_id || '-'}</Descriptions.Item>
              <Descriptions.Item label="Charge ID">{lastResult.id || '-'}</Descriptions.Item>
              <Descriptions.Item label="Provider">{lastResult.provider_key || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status inicial">{lastResult.status || '-'}</Descriptions.Item>
              <Descriptions.Item label="External charge ref">
                {lastResult.external_charge_ref || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Criada em">{formatDateTime(lastResult.created_at_local)}</Descriptions.Item>
              <Descriptions.Item label="Orientacao">
                Conferir cobranca no ASAAS Sandbox com o external charge ref acima.
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}

        {lastProviderStatus ? (
          <Card type="inner" title="Status do provider">
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Provider status">{lastProviderStatus.provider_status || '-'}</Descriptions.Item>
              <Descriptions.Item label="Normalized status">{lastProviderStatus.normalized_status || '-'}</Descriptions.Item>
              <Descriptions.Item label="Local status">{lastProviderStatus.local_status || '-'}</Descriptions.Item>
              <Descriptions.Item label="Provider charge ref">
                {lastProviderStatus.external_charge_ref || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Consultado em">
                {formatDateTime(lastProviderStatus.consulted_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Raw payload">
                <Text type="secondary">Disponivel no detalhe da chamada em Runtime History/Execution Detail.</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        ) : null}
      </Space>
    </Card>
  );
};

export default PlatformRuntimeRealChargeTester;
